/**
 * ScenarioChoiceCards — One card per pienryhmä for the scenario lock-in
 * phase (Tulevaisuuskuvien lukitseminen), filling the same role for
 * REC00025 that ParticipantWhatIfCards fills for REC00026.
 *
 * Filters Fasilitaattori segments out so only the four group statements
 * surface. Each card uses the matching scenario's left-border color, so
 * Ilmasto/Demokratia/Väestö/Teknologia stay visually linked to the
 * Tulevaisuuskuvat color system above.
 */
import React from 'react';
import styled from '@emotion/styled';
import { Quote } from 'lucide-react';
import { SITRA } from './reportStyles';
import EditableText from './EditableText';
import TranslatedText from './TranslatedText';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const Block = styled.div`
  margin-top: 24px;
`;

const Title = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${SITRA.blue};
  margin: 0 0 8px;
`;

const Hint = styled.div`
  font-size: 0.85rem;
  color: ${SITRA.textMuted};
  margin-bottom: 20px;
  font-style: italic;
`;

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  width: 100%;
`;

const Card = styled.article`
  background: ${SITRA.white};
  border: 1px solid ${SITRA.border};
  /* Per-card accent — passed inline so each scenario can carry its
   * own brand colour. Fall back to the report blue if unmatched. */
  border-left: 4px solid ${p => p.$accent || SITRA.blue};
  padding: 18px 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1 1 280px;
  min-width: 0;
  max-width: 100%;
`;

const QuoteIcon = styled.div`
  opacity: 0.55;
`;

const Body = styled.div`
  font-size: 0.95rem;
  line-height: 1.55;
  color: ${SITRA.black};
  white-space: pre-line;
`;

const Footer = styled.footer`
  font-size: 0.78rem;
  color: ${SITRA.textMuted};
  display: flex;
  gap: 10px;
  align-items: center;
`;

const Speaker = styled.span`
  font-weight: 600;
  color: ${p => p.$accent || SITRA.textBody};
`;

function formatTime(secs) {
  if (secs == null) return '';
  const s = Math.round(Number(secs));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

/**
 * Match a speaker_label like "Ilmastoryhmä" / "Demokratiaryhmä" against
 * the scenarios list to find its colour. We match by stripping the
 * "ryhmä" suffix and prefix-matching the scenario title (case-insensitive),
 * which is more robust than exact equality once labels start drifting.
 */
function findAccent(label, scenarios) {
  if (!label || !scenarios?.length) return null;
  const stem = label.toLowerCase().replace(/ryhm[aä]\s*$/, '').trim();
  if (!stem) return null;
  const hit = scenarios.find(s => (s.title || '').toLowerCase().startsWith(stem));
  return hit?.color || null;
}

// Skip segments that don't say anything substantive — single-word "Kyllä."
// / "Yes." utterances slip through speaker filtering but make terrible cards.
// Threshold = 5 real words. Even short sentences like
// "Mitä jos veroprosentti olisi 50%?" pass.
const MIN_WORDS = 5;
function hasSubstance(text) {
  if (!text) return false;
  const words = text.trim().split(/\s+/).filter(w => /\p{L}/u.test(w));
  return words.length >= MIN_WORDS;
}

export default function ScenarioChoiceCards({ segments = [], scenarios = [] }) {
  const { t } = useTranslation();
  const groupSegments = segments.filter(s => {
    const label = (s.speaker_label || '').toLowerCase();
    if (!label || label.startsWith('fasilitaattori')) return false;
    return hasSubstance(s.text);
  });

  if (groupSegments.length === 0) return null;

  return (
    <Block>
      <Title>{t('report.scenarioChoice.title')}</Title>
      <Hint>
        {t('report.scenarioChoice.hint')}
      </Hint>
      <Grid>
        {groupSegments.map(s => {
          const accent = findAccent(s.speaker_label, scenarios);
          return (
            <Card key={s.id || `${s.speaker_label}-${s.start_time}`} $accent={accent}>
              <QuoteIcon style={{ color: accent || SITRA.blue }}>
                <Quote size={18} />
              </QuoteIcon>
              <Body>
                {s.id
                  ? <TranslatedText
                      fieldKey={`transcript.${s.id}.text`}
                      fallback={s.text}
                      label={`${s.speaker_label || 'Pienryhmä'} — kommentti`}
                    />
                  : s.text}
              </Body>
              <Footer>
                <Speaker $accent={accent}>{s.speaker_label}</Speaker>
                {s.start_time != null && <span>· {formatTime(s.start_time)}</span>}
              </Footer>
            </Card>
          );
        })}
      </Grid>
    </Block>
  );
}
